import React, { useEffect } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { DICOMPatientPresentation } from '~/backend/connector-api/connector-schemas';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import PatientDetailsPanel from './PatientDetailsPanel';
import SeriesSelectionTable from './SeriesSelectionTable';

type StepSelectSeriesProps = {
    selectedPatient: DICOMPatientPresentation;
    detailedPatient: DICOMPatientPresentation | undefined;
    selectedTargetName: string;
    setSelectedTargetName: (value: string) => void;
    isLoadingStudies: boolean;
    isSeriesSelected: (patientId: string, seriesId: string) => boolean;
    toggleSeriesSelection: (
        patientId: string,
        studyInstanceUID: string,
        seriesId: string
    ) => void;
    formatDate: (date: string) => string;
    selectedImagesCount: number;
    loading: boolean;
    availableTargets: string[];
    onBackToSearch: () => void;
    onDownload: () => void;
    onLoad: () => void;
};

const StepSelectSeries = React.memo(
    ({
        selectedPatient,
        detailedPatient,
        selectedTargetName,
        setSelectedTargetName,
        isLoadingStudies,
        isSeriesSelected,
        toggleSeriesSelection,
        formatDate,
        selectedImagesCount,
        loading,
        availableTargets,
        onBackToSearch,
        onDownload: _onDownload,
        onLoad,
    }: StepSelectSeriesProps) => {
        const intl = useIntl();

        // Auto-select target if not already selected
        useEffect(() => {
            if (!selectedTargetName) {
                const targets = selectedPatient.target_names;
                if (targets && targets.length > 0) {
                    setSelectedTargetName(targets[0] || '');
                } else if (availableTargets.length > 0) {
                    setSelectedTargetName(availableTargets[0]);
                }
            }
        }, [
            selectedPatient,
            selectedTargetName,
            setSelectedTargetName,
            availableTargets,
        ]);

        return (
            <Box
                display="grid"
                gridTemplateColumns="240px 1fr"
                gap={3}
                height="100%"
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    overflow="auto"
                    minHeight={0}
                >
                    <Box>
                        <Typography variant="h5" fontWeight="600" mb={0.5}>
                            <SensitiveInfoTypography>
                                {selectedPatient.patientName ||
                                    intl.formatMessage({ id: 'unknown' })}
                            </SensitiveInfoTypography>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <SensitiveInfoTypography>
                                {selectedPatient.patientID}
                            </SensitiveInfoTypography>
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            mb={0.5}
                            display="block"
                        >
                            <FormattedMessage id="targetSource" />
                        </Typography>
                        {availableTargets.length > 1 ? (
                            <Select
                                size="small"
                                fullWidth
                                value={selectedTargetName}
                                onChange={(e) =>
                                    setSelectedTargetName(e.target.value)
                                }
                            >
                                {availableTargets.map((target) => (
                                    <MenuItem key={target} value={target}>
                                        {target}
                                    </MenuItem>
                                ))}
                            </Select>
                        ) : availableTargets.length === 1 ? (
                            <Typography variant="body2" fontWeight="500">
                                {availableTargets[0]}
                            </Typography>
                        ) : selectedPatient.target_names &&
                          selectedPatient.target_names.length > 1 ? (
                            <Select
                                size="small"
                                fullWidth
                                value={selectedTargetName}
                                onChange={(e) =>
                                    setSelectedTargetName(e.target.value)
                                }
                            >
                                {selectedPatient.target_names.map((target) => (
                                    <MenuItem key={target} value={target}>
                                        {target}
                                    </MenuItem>
                                ))}
                            </Select>
                        ) : selectedPatient.target_names &&
                          selectedPatient.target_names.length === 1 ? (
                            <Typography variant="body2" fontWeight="500">
                                {selectedPatient.target_names[0]}
                            </Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                placeholder={intl.formatMessage({
                                    id: 'dicomSourcesPlaceholder',
                                })}
                                value={selectedTargetName}
                                onChange={(e) =>
                                    setSelectedTargetName(e.target.value)
                                }
                                onKeyDown={(e) => {
                                    if (
                                        e.key === 'Enter' &&
                                        selectedTargetName
                                    ) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        )}
                    </Box>

                    <PatientDetailsPanel
                        patient={detailedPatient || selectedPatient}
                    />
                </Box>
                <Box
                    display="flex"
                    flexDirection="column"
                    height="100%"
                    minHeight={0}
                >
                    <SeriesSelectionTable
                        patient={detailedPatient || selectedPatient}
                        isSeriesSelected={isSeriesSelected}
                        toggleSeriesSelection={toggleSeriesSelection}
                        formatDate={formatDate}
                        isLoading={isLoadingStudies && !detailedPatient}
                    />
                    <Box
                        display="flex"
                        gap={2}
                        mt={2}
                        justifyContent="space-between"
                    >
                        <Button variant="outlined" onClick={onBackToSearch}>
                            <FormattedMessage id="backToSearch" />
                        </Button>
                        <Box display="flex" gap={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={selectedImagesCount === 0 || loading}
                                onClick={onLoad}
                                startIcon={
                                    loading ? (
                                        <CircularProgress size={16} />
                                    ) : undefined
                                }
                            >
                                {loading ? (
                                    <FormattedMessage id="loadingSelectedSeries" />
                                ) : (
                                    <FormattedMessage
                                        id="loadSelectedSeries"
                                        values={{
                                            count: selectedImagesCount,
                                        }}
                                    />
                                )}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    }
);
StepSelectSeries.displayName = 'StepSelectSeries';

export default StepSelectSeries;
